import { Schema } from 'effect';

/** RFC 9457 problem status range: client and server error classes only. */
const PROBLEM_STATUS_MIN = 400;
const PROBLEM_STATUS_MAX = 599;
const TRACE_ID = /^[0-9a-f]{32}$/u;
const UNTRACED = /^0+$/u;

// Zod's `z.iso.datetime({ offset: true })` pattern: a calendar-valid date, seconds, optional
// fraction, and `Z` or a `±hh:mm` offset. A filter rather than `isPattern`, so the published
// documents keep `format: date-time` without a pattern.
const ISO_OFFSET_DATE_TIME =
  /^(?:(?:\d\d[2468][048]|\d\d[13579][26]|\d\d0[48]|[02468][048]00|[13579][26]00)-02-29|\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\d|30)|02-(?:0[1-9]|1\d|2[0-8])))T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/u;

const Text = Schema.String.check(Schema.isMinLength(1));

/**
 * A W3C Trace Context trace id: 32 lowercase hex digits, not the all-zero id the
 * specification defines as invalid.
 */
// The all-zero refusal is a filter rather than part of the pattern, so the published documents keep
// the plain 32-hex pattern every validator reads.
export const TraceId = Schema.String.check(
  Schema.isPattern(TRACE_ID),
  Schema.makeFilter((value) => !UNTRACED.test(value) || 'a trace id other than the all-zero one'),
);
export type TraceId = typeof TraceId.Type;

/**
 * The problem+json error body with Finnest's extensions, as an Effect
 * contract: the same fields and checks as `ProblemDetail` in
 * `@finnest/core/schemas/http`.
 */
export const ProblemDetail = Schema.Struct({
  title: Text,
  status: Schema.Int.check(
    Schema.isBetween({ minimum: PROBLEM_STATUS_MIN, maximum: PROBLEM_STATUS_MAX }),
  ),
  detail: Schema.optionalKey(Text),
  instance: Schema.optionalKey(Text),
  type: Schema.optionalKey(Text),
  code: Text,
  traceId: Schema.optionalKey(TraceId),
  requestDateTime: Schema.String.annotate({ format: 'date-time' }).check(
    Schema.makeFilter(
      (value) => ISO_OFFSET_DATE_TIME.test(value) || 'an ISO 8601 date-time with an offset',
    ),
  ),
  errors: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        field: Schema.optionalKey(Text),
        code: Schema.optionalKey(Text),
        message: Text,
      }),
    ),
  ),
});
export type ProblemDetail = typeof ProblemDetail.Type;
